import { Component, HostListener, OnInit, OnDestroy, OnChanges, SimpleChanges, inject, Input, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, NavigationEnd } from '@angular/router';
import { KeycloakService, KeycloakEventType } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToasterService } from '../../../services/toaster.service';
import { environment } from '../../../../environments/environment';
import { LogoutService } from '../../../services/logout.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { AccessibilityPanelComponent } from '../accessibility-panel/accessibility-panel.component';
import { RolesApiService, AuthApiService, Role } from '@angular-workspace/services';
import { ModalComponent } from '../primeng';
import { LoginModalComponent } from '../login-modal/login-modal.component';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, RouterModule, AccessibilityPanelComponent, ModalComponent, LoginModalComponent],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit, OnDestroy, OnChanges {
  private keycloak = inject(KeycloakService);
  private logoutService = inject(LogoutService);
  private toasterService = inject(ToasterService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private rolesService = inject(RolesApiService);
  private authApiService = inject(AuthApiService);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);
  private keycloakSubscription?: Subscription;
  
  @Input() forceLightBackground: boolean = false; // Allow parent to force light background
  
  isScrolled = false;
  isDarkBackground = true; // Default to dark since banner has dark overlay
  isMobileMenuOpen = false;
  isLoggedIn = false;
  userName = '';
  userEmail = '';
  userInitials = '';
  isUserDropdownOpen = false;
  isAccessibilityPanelOpen = false;
  showLoginModal = false;
  mySubmissionsLoginRedirectUri = '';

  private _isUpgradeModalVisible = false;
  hasProviderRole = false;
  hasComputeRole = false;
  isRoleRequestSent: { [key: string]: boolean } = {};
  hasPendingProviderRole = false;
  hasPendingComputeRole = false;
  showOrganisationNote = false;
  showPendingOrganisationNote = false; // Track if we should show pending message
  showGrantedOrganisationNote = false; // Track if we should show granted message
  hasOrganisation = false; // Track if user has an organisation object
  organisationStatus: string | null = null; // Track organisation status
  currentRoute = '';
  readonly contactUsComposeUrl = environment.hackathonSupportGmailComposeUrl;
  
  // Role definitions
  roles = [
    {
      id: 'provider',
      name: 'Provider',
      title: 'Provider',
      description: 'Publish and manage datasets and models on NHA platform.',
      icon: 'M12 2L2 7L12 12L22 7L12 2ZM2 17L12 22L22 17M2 12L12 17L22 12',
      features: [
        'Manage Datasets and Models',
        'Submit use case and suggest associated datasets or Models'
      ],
      color: '#10b981' // primary green
    },
    {
      id: 'compute',
      name: 'Compute',
      title: 'Compute ',
      description: 'Run AI experiments and provide computational resources for data processing.',
      icon: 'M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01',
      features: [
        'Run AI experiments and models',
        'Provide computational resources'
      ],
      color: '#10b981' // primary green - same as provider
    }
  ];

  get isUpgradeModalVisible(): boolean {
    return this._isUpgradeModalVisible;
  }

  set isUpgradeModalVisible(value: boolean) {
    this._isUpgradeModalVisible = value;
    
    // Remove PrimeNG's body padding-right when modal opens/closes
    if (typeof document !== 'undefined') {
      const body = document.body;
      if (value) {
        // Modal opening - remove padding-right immediately and continuously
        body.style.paddingRight = '0';
        
        // Use MutationObserver to watch for PrimeNG adding padding-right
        // and remove it immediately
        const observer = new MutationObserver(() => {
          if (body.style.paddingRight && body.style.paddingRight !== '0px') {
            body.style.paddingRight = '0';
          }
        });
        
        observer.observe(body, {
          attributes: true,
          attributeFilter: ['style', 'class']
        });
        
        // Fallback check at reasonable interval (was 10ms - caused slowness in incognito)
        const intervalId = setInterval(() => {
          if (body.style.paddingRight && body.style.paddingRight !== '0px') {
            body.style.paddingRight = '0';
          }
          if (!this._isUpgradeModalVisible) {
            clearInterval(intervalId);
            observer.disconnect();
          }
        }, 300);
        
        // Store observer and interval for cleanup
        (this as any)._paddingObserver = observer;
        (this as any)._paddingInterval = intervalId;
      } else {
        // Modal closing - cleanup observers and clear global flag (prevents duplicate modals when multiple headers exist)
        if (typeof window !== 'undefined' && (window as any).__nhaUpgradeModalOpen) {
          (window as any).__nhaUpgradeModalOpen = false;
        }
        if ((this as any)._paddingObserver) {
          (this as any)._paddingObserver.disconnect();
        }
        if ((this as any)._paddingInterval) {
          clearInterval((this as any)._paddingInterval);
        }
      }
    }
    
    // Re-check provider role and organisation when modal becomes visible
    if (value) {
      this.checkProviderRole();
      this.checkOrganisationId();
      // Reset request sent state when opening modal
      this.isRoleRequestSent = {};
    } else {
      // Reset request sent state when closing modal (optional, for next time)
      // Keep it true if you want to persist the state
    }
  }

  async ngOnInit(): Promise<void> {
    this.checkBackground();
    this.updateCurrentRoute();
    
    // Listen to route changes to update background and login status
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(async () => {
        this.checkBackground();
        this.updateCurrentRoute();
        // Re-check login status on route change to ensure header updates correctly
        await this.updateLoginStatus();
      });
    
    // Listen for custom event to open upgrade modal from other components
    window.addEventListener('openUpgradeModal', this.handleOpenUpgradeModalEvent.bind(this));
    
    // Check initial login status
    await this.updateLoginStatus();
    
    // Also check again after a short delay to catch any race conditions
    setTimeout(async () => {
      await this.updateLoginStatus();
    }, 100);

    // Subscribe to Keycloak events to update login status dynamically
    this.keycloakSubscription = this.keycloak.keycloakEvents$.subscribe(
      async (event: unknown) => {
        const keycloakEvent = event as { type: KeycloakEventType };
        if (keycloakEvent.type === KeycloakEventType.OnAuthSuccess) {
          await this.updateLoginStatus();
        } else if (
          keycloakEvent.type === KeycloakEventType.OnAuthRefreshSuccess ||
          keycloakEvent.type === KeycloakEventType.OnAuthLogout
        ) {
          await this.updateLoginStatus();
        }
      }
    );

    // Listen for storage changes (when userData is updated)
    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-check background when input changes
    if (changes['forceLightBackground']) {
      this.checkBackground();
    }
  }

  ngOnDestroy(): void {
    // Remove event listener
    window.removeEventListener('openUpgradeModal', this.handleOpenUpgradeModalEvent.bind(this));
    // Unsubscribe to prevent memory leaks
    if (this.keycloakSubscription) {
      this.keycloakSubscription.unsubscribe();
    }
    // Remove storage event listener
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  /**
   * Handle storage changes (when userData is updated)
   */
  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'userData') {
      this.checkProviderRole();
      this.checkOrganisationId();
    }
  }

  private async updateLoginStatus(): Promise<void> {
    try {
      // Check if user is logged in
      const wasLoggedIn = this.isLoggedIn;
      this.isLoggedIn = await this.keycloak.isLoggedIn();
      
      console.log('Login status updated:', this.isLoggedIn);

      // Get user name and email if logged in
      if (this.isLoggedIn) {
        try {
          const userProfile = await this.keycloak.loadUserProfile();
          this.userName = userProfile.firstName || userProfile.username || 'User';
          this.userEmail = userProfile.email || '';

          // Generate initials from Keycloak profile as a fallback
          const profileFirst = userProfile.firstName || '';
          const profileLast = userProfile.lastName || '';
          if (profileFirst && profileLast) {
            this.userInitials = `${profileFirst.charAt(0)}${profileLast.charAt(0)}`.toUpperCase();
          } else if (profileFirst) {
            this.userInitials = profileFirst.substring(0, 2).toUpperCase();
          } else if (userProfile.username) {
            this.userInitials = userProfile.username.substring(0, 2).toUpperCase();
          } else {
            this.userInitials = 'U';
          }

          // Try to refine initials from userData stored by AuthListener (backend user info)
          try {
            const userDataStr = localStorage.getItem('userData');
            if (userDataStr) {
              const rawUserData = JSON.parse(userDataStr);
              const actualUserData = rawUserData?.result || rawUserData || {};
              const fn =
                actualUserData.firstName ||
                actualUserData.first_name ||
                actualUserData.first_name_en ||
                profileFirst ||
                '';
              const ln =
                actualUserData.lastName ||
                actualUserData.last_name ||
                actualUserData.last_name_en ||
                profileLast ||
                '';

              // Prefer first + last word from full name when available (e.g. "S Babu" -> "SB")
              const fullName: string =
                actualUserData.name ||
                actualUserData.fullName ||
                `${fn} ${ln}`.trim() ||
                this.userName;

              if (fullName && typeof fullName === 'string') {
                const parts = fullName.trim().split(/\s+/);
                if (parts.length >= 2) {
                  const firstInitial = parts[0].charAt(0);
                  const lastInitial = parts[parts.length - 1].charAt(0);
                  this.userInitials = `${firstInitial}${lastInitial}`.toUpperCase();
                } else if (parts.length === 1) {
                  this.userInitials = parts[0].substring(0, 2).toUpperCase();
                }
              } else if (fn && ln) {
                this.userInitials = `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase();
              }
            }
          } catch {
            // Ignore userData parsing errors – keep Keycloak-derived initials
          }

          // Check provider role from user data
          this.checkProviderRole();
          
          console.log('User profile loaded:', this.userName, this.userEmail, this.userInitials);
        } catch (error) {
          console.error('Error loading user profile:', error);
          this.userName = 'User';
          this.userEmail = '';
          this.userInitials = 'U';
          this.hasProviderRole = false;
        }
      } else {
        this.userName = '';
        this.userEmail = '';
        this.userInitials = '';
        this.hasProviderRole = false;
      }
      
      // Always trigger change detection to ensure view updates
      // Use setTimeout to ensure it runs after the current execution cycle
      setTimeout(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }, 0);
    } catch (error) {
      console.error('Error checking login status:', error);
      this.isLoggedIn = false;
      this.userName = '';
      this.userEmail = '';
      this.userInitials = '';
      this.hasProviderRole = false;
    }
  }

  /**
   * Get user roles from localStorage (userData)
   */
  private getUserRoles(): string[] {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        
        // Check if data is nested in result property
        const actualUserData = userData?.result || userData;
        
        if (actualUserData && Array.isArray(actualUserData.roles)) {
          return actualUserData.roles;
        }
      }
    } catch (error) {
      console.error('Error reading user data from localStorage:', error);
    }
    return [];
  }

  /**
   * Check if user has cos_admin role
   * @returns true if user has cos_admin role, false otherwise
   */
  get isCosAdmin(): boolean {
    const userRoles = this.getUserRoles();
    return userRoles.includes('cos_admin') || userRoles.includes('cos-admin');
  }

  /**
   * Check if user has org_admin role
   * @returns true if user has org_admin role, false otherwise
   */
  get isOrgAdmin(): boolean {
    const userRoles = this.getUserRoles();
    return userRoles.includes('org_admin') || userRoles.includes('org-admin');
  }

  /**
   * Check if user has admin or platform-admin role
   * @returns true if user has admin/platform-admin role, false otherwise
   */
  get isAdmin(): boolean {
    const userRoles = this.getUserRoles();
    const normalizedRoles = userRoles.map(role => role.toLowerCase());
    return normalizedRoles.includes('admin') || 
           normalizedRoles.includes('platform-admin') || 
           normalizedRoles.includes('platform_admin') ||
           normalizedRoles.includes('cos_admin') ||
           normalizedRoles.includes('cos-admin');
  }

  /**
   * Check if upgrade role should be disabled.
   * Disabled only when user has BOTH provider AND compute roles; otherwise enabled.
   * (Admin users do not see the button at all – hidden via *ngIf="!isAdmin" in template.)
   * @returns true if upgrade should be disabled, false if it should be enabled
   */
  get isUpgradeDisabled(): boolean {
    try {
      const userRoles = this.getUserRoles();
      if (!userRoles || userRoles.length === 0) {
        return false;
      }
      const normalized = userRoles.map(r => r.toLowerCase());
      const hasProvider = normalized.includes('provider');
      const hasCompute = normalized.includes('compute');
      // Disable only when user has both provider and compute
      return hasProvider && hasCompute;
    } catch (error) {
      console.error('Error checking roles for upgrade button:', error);
      return false;
    }
  }

  /**
   * Check if user has provider role (testing with consumer role)
   */
  private checkProviderRole(): void {
    const roles = this.getUserRoles();
    // For testing: check for consumer role instead of provider
    this.hasProviderRole = roles.some(role => role.toLowerCase() === 'provider');
    console.log('Role check (testing with consumer):', this.hasProviderRole, 'Roles:', roles);
  }

  /**
   * Check if user has organisationId or organisation object
   */
  private checkOrganisationId(): void {
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const actualUserData = userData?.result || userData;
        
        // Check if organisationId exists and is not empty/null
        const organisationId = actualUserData?.organisationId;
        
        // Check if organisation object exists
        const organisation = actualUserData?.organisation || actualUserData?.organization;
        
        // Extract organisation status if organisation object exists
        if (organisation) {
          this.organisationStatus = organisation.status || organisation['status'] || null;
          // Normalize status to lowercase for comparison
          const normalizedStatus = this.organisationStatus ? this.organisationStatus.toLowerCase().trim() : null;
          
          // User has organisation if organisation object exists
          this.hasOrganisation = true;
          
          // Show pending message if status is "pending"
          if (normalizedStatus === 'pending') {
            this.showPendingOrganisationNote = true;
            this.showOrganisationNote = false;
            this.showGrantedOrganisationNote = false;
          } 
          // Show granted message if status is "granted"
          else if (normalizedStatus === 'granted') {
            this.showPendingOrganisationNote = false;
            this.showOrganisationNote = false;
            this.showGrantedOrganisationNote = true;
          }
          // Show "requires organisation" note for other statuses (rejected, etc.)
          else {
            this.showPendingOrganisationNote = false;
            this.showOrganisationNote = true;
            this.showGrantedOrganisationNote = false;
          }
        } else if (organisationId) {
          // If only organisationId exists (no organisation object), consider as having organisation
          this.hasOrganisation = true;
          this.organisationStatus = null;
          // Show note if only organisationId exists (no status to check)
          this.showOrganisationNote = true;
          this.showPendingOrganisationNote = false;
          this.showGrantedOrganisationNote = false;
        } else {
          // No organisation at all
          this.hasOrganisation = false;
          this.organisationStatus = null;
          this.showOrganisationNote = true;
          this.showPendingOrganisationNote = false;
          this.showGrantedOrganisationNote = false;
        }
        
        console.log('Organisation check:', {
          organisationId,
          organisation: !!organisation,
          organisationStatus: this.organisationStatus,
          hasOrganisation: this.hasOrganisation,
          showNote: this.showOrganisationNote
        });
      } else {
        // If no userData, show the note
        this.hasOrganisation = false;
        this.organisationStatus = null;
        this.showOrganisationNote = true;
        this.showPendingOrganisationNote = false;
        this.showGrantedOrganisationNote = false;
      }
    } catch (error) {
      console.error('Error checking organisationId:', error);
      // On error, show the note to be safe
      this.hasOrganisation = false;
      this.organisationStatus = null;
      this.showOrganisationNote = true;
      this.showPendingOrganisationNote = false;
      this.showGrantedOrganisationNote = false;
    }
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    // Check if user has scrolled more than 50px
    this.isScrolled = window.scrollY > 50;
    this.checkBackground();
  }

  private checkBackground() {
    // If parent component forces light background, use that
    if (this.forceLightBackground) {
      this.isDarkBackground = false;
      return;
    }
    
    // Check current route - if on pages with light background, use light theme
    const currentRoute = this.router.url;
    const lightBackgroundRoutes = ['/datasets', '/dataset', '/models', '/usecases', '/leadership', '/my-submissions', '/discussions', '/canvas', '/knowledge-library', '/knowledge-hub', '/contact-us'];
    if (lightBackgroundRoutes.some(route => currentRoute.startsWith(route))) {
      this.isDarkBackground = false;
      return;
    }
    
    // Logic to detect background color
    // For now, we'll use scroll position as a simple heuristic
    // When at top of page (banner section), use transparent with white text
    // When scrolled, use dark background
    this.isDarkBackground = window.scrollY < 100;
  }

  private updateCurrentRoute() {
    this.currentRoute = this.router.url;
  }

  isDatasetsPageActive(): boolean {
    return this.currentRoute.startsWith('/datasets') || this.currentRoute.startsWith('/dataset/');
  }

  isUsecasesPageActive(): boolean {
    return this.currentRoute.startsWith('/usecases') || this.currentRoute.startsWith('/usecase/');
  }

  isReferenceDatasetsPageActive(): boolean {
    return this.currentRoute.startsWith('/reference');
  }

  isLeadershipPageActive(): boolean {
    return this.currentRoute.startsWith('/leadership');
  }

  isMySubmissionsPageActive(): boolean {
    return this.currentRoute.startsWith('/my-submissions');
  }

  async onMySubmissionsClick(event: Event): Promise<void> {
    event.preventDefault();
    this.closeMobileMenu();
    try {
      const isLoggedIn = await this.keycloak.isLoggedIn();
      this.isLoggedIn = isLoggedIn;
      if (!isLoggedIn) {
        this.mySubmissionsLoginRedirectUri = `${window.location.origin}/my-submissions`;
        this.showLoginModal = true;
        return;
      }
      this.router.navigate(['/my-submissions']);
    } catch {
      this.mySubmissionsLoginRedirectUri = `${window.location.origin}/my-submissions`;
      this.showLoginModal = true;
    }
  }

  isDiscussionsPageActive(): boolean {
    return this.currentRoute.startsWith('/discussions');
  }

  async onLogin() {
    try {
      console.log('Login button clicked');
      
      // Wait for Keycloak to be initialized
      let keycloakInstance = this.keycloak.getKeycloakInstance();
      if (!keycloakInstance) {
        // Wait a bit for Keycloak to initialize
        await new Promise(resolve => setTimeout(resolve, 100));
        keycloakInstance = this.keycloak.getKeycloakInstance();
      }
      
      if (!keycloakInstance) {
        console.error('Keycloak instance not available');
        this.toasterService.error('Authentication service is not ready. Please refresh the page.');
        return;
      }
      
      // Use current URL as redirectUri - Keycloak expects the full URL
      // Clean the URL first to remove any error parameters from previous attempts
      const cleanUrl = window.location.origin + window.location.pathname;
      console.log('Redirecting to Keycloak login with redirectUri:', cleanUrl);
      
      // Call login - it redirects the browser, so we don't need to await
      this.keycloak.login({
        redirectUri: cleanUrl
      }).catch((error) => {
        console.error('Error during login:', error);
        this.toasterService.error('Failed to initiate login. Please try again.');
      });
    } catch (error) {
      console.error('Error during login:', error);
      this.toasterService.error('Failed to initiate login. Please try again.');
    }
  }

  onRegister() {
    this.googleAnalytics.trackRegistration('Keycloak');
    this.keycloak?.register({
      redirectUri: window.location.href
    });
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen = !this.isMobileMenuOpen;
  }

  closeMobileMenu() {
    this.isMobileMenuOpen = false;
  }

  onLogout() {
    this.logoutService.logout(window.location.origin);
  }

  toggleUserDropdown(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.isUserDropdownOpen = !this.isUserDropdownOpen;
    // Trigger change detection to ensure dropdown visibility updates
    this.cdr.markForCheck();
    this.cdr.detectChanges();
  }

  closeUserDropdown() {
    this.isUserDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    // Close user dropdown when clicking outside
    if (this.isUserDropdownOpen) {
      const dropdownContainer = target.closest('.user--dropdown-container');
      const isDropdownButton = target.closest('button.dropdown--link') || target.closest('a.dropdown--link');
      if (!dropdownContainer && !isDropdownButton) {
        this.isUserDropdownOpen = false;
        this.cdr.markForCheck();
      }
    }
  }

  toggleAccessibilityPanel() {
    this.isAccessibilityPanelOpen = !this.isAccessibilityPanelOpen;
    // Close user dropdown if open
    if (this.isUserDropdownOpen) {
      this.isUserDropdownOpen = false;
    }
  }

  closeAccessibilityPanel() {
    this.isAccessibilityPanelOpen = false;
  }

  /**
   * Handle click on Upgrade Your Role button
   */
  onUpgradeRoleClick(event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (!this.isUpgradeDisabled) {
      this.openUpgradeModal();
    }
  }

  /**
   * Open upgrade role modal
   */
  async openUpgradeModal() {
    // Prevent duplicate modals when multiple header instances exist (e.g. app header + route header)
    if (typeof window !== 'undefined' && (window as any).__nhaUpgradeModalOpen) {
      return;
    }
    (window as any).__nhaUpgradeModalOpen = true;
    console.log('Opening upgrade modal...');
    
    // Close dropdown first to prevent event conflicts
    this.closeUserDropdown();
    
    // Use setTimeout to ensure dropdown closes before modal opens
    // This prevents the document click handler from interfering
    setTimeout(() => {
      // Check if user has provider role from userData in localStorage
      this.checkProviderRole();
      this.checkOrganisationId();
      
      // Reset request sent state when opening modal
      this.isRoleRequestSent = {};
      
      // Open modal
      this.isUpgradeModalVisible = true;
      
      // Trigger change detection to ensure modal opens
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Call User API to check for pending roles (after modal is visible)
      this.checkPendingRoles().then(() => {
        console.log('Modal visibility set to:', this.isUpgradeModalVisible, 'Has provider role:', this.hasProviderRole, 'Has pending provider role:', this.hasPendingProviderRole);
        // Trigger change detection after API call completes
        this.cdr.markForCheck();
        this.cdr.detectChanges();
      }).catch((error) => {
        console.error('Error in openUpgradeModal:', error);
      });
    }, 0);
  }

  /**
   * Check for pending roles by calling User API
   */
  private async checkPendingRoles(): Promise<void> {
    try {
      console.log('Calling getUserInfo API...');
      const userData = await this.authApiService.getUserInfo();
      console.log('User API response received:', userData);
      
      // Extract result if nested, otherwise use response directly
      const actualUserData = userData?.['result'] || userData;
      console.log('Actual user data:', actualUserData);
      
      // Check user's current roles
      const userRoles = actualUserData?.roles || [];
      this.hasProviderRole = userRoles.some((role: string) => role.toLowerCase() === 'provider');
      this.hasComputeRole = userRoles.some((role: string) => role.toLowerCase() === 'compute');
      
      // Check if pendingRoles exists and contains "provider" or "compute"
      if (actualUserData?.pendingRoles && Array.isArray(actualUserData.pendingRoles)) {
        this.hasPendingProviderRole = actualUserData.pendingRoles.includes('provider');
        this.hasPendingComputeRole = actualUserData.pendingRoles.includes('compute');
        console.log('Pending roles check:', {
          pendingRoles: actualUserData.pendingRoles,
          hasPendingProviderRole: this.hasPendingProviderRole,
          hasPendingComputeRole: this.hasPendingComputeRole
        });
      } else {
        this.hasPendingProviderRole = false;
        this.hasPendingComputeRole = false;
        console.log('No pendingRoles found in user data. Actual user data keys:', Object.keys(actualUserData || {}));
      }
    } catch (error) {
      console.error('Error checking pending roles:', error);
      // On error, don't disable the button (assume no pending role)
      this.hasPendingProviderRole = false;
      this.hasPendingComputeRole = false;
    }
  }

  /**
   * Handle upgrade role confirmation
   */
  handleUpgradeConfirm() {
    // TODO: Add upgrade logic here
    this.toasterService.success('Upgrade request submitted!', 3000);
  }

  /**
   * Handle role selection from modal
   */
  async handleRoleSelection(roleId: string) {
    console.log('Role selected:', roleId);
    
    try {
      let response: any;
      
      // Call the appropriate API based on role type
      if (roleId === 'compute') {
        // Call compute request API for compute role
        response = await this.authApiService.computeRequest();
      } else {
        // Refresh token BEFORE making provider role request
        try {
          const refreshed = await this.keycloak.updateToken(5);
          if (refreshed) {
            console.log('Token refreshed successfully before provider role request');
            // Update token in localStorage
            const token = await this.keycloak.getToken();
            if (token) {
              localStorage.setItem('iudx-ui-cat-auth-token', 'Bearer ' + token);
            }
          } else {
            console.log('Token was still valid, no refresh needed');
            // Still update token in localStorage to ensure it's current
            const token = await this.keycloak.getToken();
            if (token) {
              localStorage.setItem('iudx-ui-cat-auth-token', 'Bearer ' + token);
            }
          }
        } catch (tokenError) {
          console.error('Error refreshing token before provider role request:', tokenError);
          // Continue with request even if token refresh fails (token might still be valid)
        }
        
        // Call role upgrade API for other roles (e.g., provider) after token refresh
        response = await this.authApiService.requestRoleUpgrade(roleId);
      }
      
      console.log('Role upgrade request successful:', response);
      
      // Mark request as sent for this specific role
      this.isRoleRequestSent[roleId] = true;
      
      // Always use our custom success message for role upgrade requests
      const successMessage = 'Your request to Upgrade role is Successful';
      
      // Show success message
      this.toasterService.success(successMessage, 4000);
      
      // Refresh pending roles after successful request
      await this.checkPendingRoles();
      
      // Auto-close the upgrade modal on 200 OK
      this.isUpgradeModalVisible = false;
      
      // Notify other components (e.g. My Projects Request Credits) to refresh role state
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('userRolesUpdated', { detail: { roleId } }));
      }
    } catch (error: any) {
      console.error('Error requesting role upgrade:', error);
      
      // Reset request sent state on error
      this.isRoleRequestSent[roleId] = false;
      
      // Show error message
      const errorMessage = error?.error?.detail || error?.error?.message || error?.message || 'Failed to submit role upgrade request. Please try again.';
      this.toasterService.error(errorMessage, 4000);
    }
  }

  /**
   * Get button text for a role
   */
  getRoleButtonText(roleId: string): string {
    if (roleId === 'provider' && this.hasProviderRole) {
      return 'Role Granted';
    }
    if (roleId === 'compute' && this.hasComputeRole) {
      return 'Role Granted';
    }
    if (this.isRoleRequestSent[roleId]) {
      return 'Request Sent';
    }
    // Note: "Request Pending" is now shown as a badge, not in button text
    return 'Request Upgrade';
  }

  /**
   * Check if role button should be disabled
   */
  isRoleButtonDisabled(roleId: string): boolean {
    // For provider role: disable if user doesn't have an organisation (showOrganisationNote is true)
    // This prevents API calls for provider role upgrade without organisation membership
    if (roleId === 'provider' && this.showOrganisationNote) {
      return true;
    }
    // Disable button if organisation request is pending for provider role
    if (roleId === 'provider' && this.showPendingOrganisationNote) {
      return true;
    }
    if (roleId === 'provider' && this.hasProviderRole) {
      return true;
    }
    if (roleId === 'compute' && this.hasComputeRole) {
      return true;
    }
    if (this.isRoleRequestSent[roleId]) {
      return true;
    }
    if (roleId === 'provider' && this.hasPendingProviderRole) {
      return true;
    }
    if (roleId === 'compute' && this.hasPendingComputeRole) {
      return true;
    }
    return false;
  }

  /**
   * Navigate to user's dashboard based on their highest role
   */
  navigateToDashboard() {
    try {
      if (!this.isLoggedIn) {
        // If not logged in, redirect to home/landing page
        this.router.navigate(['/']);
        return;
      }

      // Get user roles from localStorage (userData)
      const userRoles = this.getUserRoles();
      
      if (!userRoles || userRoles.length === 0) {
        console.warn('No roles found in userData, falling back to consumer panel');
        this.router.navigate(['/consumer/home']);
        return;
      }

      // Get the panel route based on highest role using RolesApiService
      const panelRoute = this.rolesService.getHighestRolePanelRoute(userRoles);

      // Navigate to the appropriate panel
      this.router.navigate([panelRoute]);
    } catch (error) {
      console.error('Error navigating to dashboard:', error);
      // Fallback to consumer panel on error
      this.router.navigate(['/consumer/home']);
    }
  }

  /**
   * Navigate to user's profile page based on highest role
   * Syntax: <panelname>/profile
   * Examples: cos_admin -> /admin/profile
   *          org-admin -> /org-admin/profile
   *          provider -> /provider/profile
   *          consumer -> /consumer/profile
   */
  navigateToProfile() {
    this.closeUserDropdown();
    
    try {
      if (!this.isLoggedIn) {
        // If not logged in, redirect to home/landing page
        this.router.navigate(['/']);
        return;
      }

      // Get user roles and determine highest role
      const userRoles = this.getUserRoles();
      
      if (userRoles && userRoles.length > 0) {
        // Get the highest priority role
        const highestRole = this.rolesService.getHighestRole(userRoles);
        
        if (highestRole) {
          // Map role to profile route
          const roleProfileRoutes: Record<string, string> = {
            'admin': '/admin/profile',
            'org-admin': '/org-admin/profile',
            'provider': '/provider/profile',
            'consumer': '/consumer/profile'
          };
          
          const profileRoute = roleProfileRoutes[highestRole];
          
          if (profileRoute) {
            console.log('Profile navigation - Highest role:', highestRole);
            console.log('Profile navigation - Profile route:', profileRoute);
            this.router.navigate([profileRoute]);
            return;
          }
        }
      }
      
      // Fallback to consumer profile if no roles found
      console.warn('No roles found, falling back to consumer profile');
      this.router.navigate(['/consumer/profile']);
    } catch (error) {
      console.error('Error navigating to profile:', error);
      // Fallback to consumer profile on error
      this.router.navigate(['/consumer/profile']);
    }
  }

  /**
   * Navigate to support page based on highest role
   * Syntax: /<highest-role>/support
   * Examples: cos_admin -> /admin/support
   *          org-admin -> /org-admin/support
   *          provider -> /provider/support
   *          consumer -> /consumer/support
   */
  navigateToSupport() {
    this.closeUserDropdown();
    
    try {
      if (!this.isLoggedIn) {
        // If not logged in, redirect to home/landing page
        this.router.navigate(['/']);
        return;
      }

      // Get user roles and determine highest role
      const userRoles = this.getUserRoles();
      
      if (userRoles && userRoles.length > 0) {
        // Get the highest priority role
        const highestRole = this.rolesService.getHighestRole(userRoles);
        
        if (highestRole) {
          // Map role to support route
          const roleSupportRoutes: Record<string, string> = {
            'admin': '/admin/support',
            'org-admin': '/org-admin/support',
            'provider': '/provider/support',
            'consumer': '/consumer/support'
          };
          
          const supportRoute = roleSupportRoutes[highestRole];
          
          if (supportRoute) {
            console.log('Support navigation - Highest role:', highestRole);
            console.log('Support navigation - Support route:', supportRoute);
            this.router.navigate([supportRoute]);
            return;
          }
        }
      }
      
      // Fallback to consumer support if no roles found
      console.warn('No roles found, falling back to consumer support');
      this.router.navigate(['/consumer/support']);
    } catch (error) {
      console.error('Error navigating to support:', error);
      // Fallback to consumer support on error
      this.router.navigate(['/consumer/support']);
    }
  }

  /**
   * Handle custom event to open upgrade modal.
   * openUpgradeModal() uses a global flag so only one header instance opens when multiple exist (e.g. app + route header).
   */
  private handleOpenUpgradeModalEvent(event: Event): void {
    const customEvent = event as CustomEvent;
    console.log('Received openUpgradeModal event:', customEvent.detail);
    this.openUpgradeModal();
  }
}
